import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { cursorGlow } from '@schmancy/directives/cursor-glow'
import { ifDefined } from 'lit/directives/if-defined.js'
import { fromEvent, merge, takeUntil } from 'rxjs'
import { filter } from 'rxjs/operators'

// What a consumer can slot into a card that carries its own activation: the
// native controls, the ARIA roles that imply one, and any element the consumer
// made a tab stop. The last two entries are what catch a slotted control that
// is not a `<button>` — a role-carrying div, a focusable custom element.
const NESTED_CONTROL_SELECTOR = [
	'a[href]',
	'area[href]',
	'button',
	'input',
	'select',
	'textarea',
	'summary',
	'label',
	'[role="button"]',
	'[role="link"]',
	'[role="checkbox"]',
	'[role="switch"]',
	'[role="radio"]',
	'[role="tab"]',
	'[role="option"]',
	'[role="menuitem"]',
	'[role="menuitemcheckbox"]',
	'[role="menuitemradio"]',
	'[role="combobox"]',
	'[role="textbox"]',
	'[role="searchbox"]',
	'[role="slider"]',
	'[role="spinbutton"]',
	'[contenteditable]:not([contenteditable="false"])',
	'[tabindex]:not([tabindex="-1"])',
].join(',')

@customElement('schmancy-card')
export default class SchmancyCard extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				position: relative;
				/* rounded-xl rung — 14px, matches the inner content's rounded-xl */
				border-radius: var(--schmancy-radius-xl, 0.875rem);
				transition:
					box-shadow var(--schmancy-sys-motion-duration-short4) var(--schmancy-sys-motion-easing-standard),
					transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
				outline: none;
			}

			:host([type='elevated']) {
				background-color: var(--schmancy-sys-color-surface-low);
				box-shadow: var(--schmancy-sys-elevation-1);
			}
			:host([type='filled']) {
				background-color: var(--schmancy-sys-color-surface-highest);
				box-shadow: var(--schmancy-sys-elevation-0);
			}
			/* The edge is an OUTLINE, not a border, and that is load-bearing rather
			   than stylistic. Every consumer of this component is a shadow root
			   carrying schmancy's baked Tailwind sheet, whose preflight opens with
			   *, ::after, ::before, ::backdrop, ::file-selector-button { border: 0 solid }
			   — a rule that matches this host from the OUTER tree. The shadow-DOM
			   cascade sorts encapsulation context BEFORE layers and before
			   specificity, so for normal declarations the outer tree always wins: a
			   border declared here loses everywhere the component actually ships,
			   and outlined renders indistinguishable from no variant at all with
			   nothing thrown. outline is the one edge-painting property that
			   universal rule never touches; outline-offset: -1px draws it exactly
			   where the border sat, and an outline follows the host's
			   border-radius as a border does. This rule is (0,2,0) and the
			   focus-visible ring below is (0,4,0), so the ring still wins when the
			   card takes keyboard focus. */
			:host([type='outlined']) {
				background-color: var(--schmancy-sys-color-surface-default);
				outline: 1px solid var(--schmancy-sys-color-outlineVariant);
				outline-offset: -1px;
				box-shadow: var(--schmancy-sys-elevation-0);
			}

			/* chromeless — the card as an interaction box and nothing else, for
			   content that is already its own surface (a poster, a full-bleed
			   image). Background, elevation, edge and hover lift are each scoped to
			   a named type above, so this variant declares none of them; the corner
			   is the one thing the base :host asserts unconditionally, and a card
			   contributing no surface has no corner to contribute either. Leaving
			   the xl rung here would round the focus ring and the state layer around
			   content whose own corner is the only one that exists — two corners for
			   one shape, with nothing keeping them in agreement. A consumer's own
			   rounded-* on the host still wins from the outer tree, and every layer
			   inside follows it (rounded-[inherit] in render). */
			:host([type='chromeless']) {
				border-radius: 0;
			}

			:host([interactive]) {
				cursor: pointer;
				-webkit-tap-highlight-color: transparent;
			}
			:host([interactive]:focus-visible:not([disabled])) {
				outline: 2px solid var(--schmancy-sys-color-primary-default);
				outline-offset: 2px;
			}
			:host([disabled]) {
				pointer-events: none;
				opacity: var(--schmancy-sys-state-opacity-disabled, 0.38);
			}

			:host([type='elevated'][interactive]:hover:not([disabled])) {
				box-shadow:
					var(--schmancy-sys-elevation-2),
					0 4px 24px -6px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 12%, transparent);
				transform: translateY(-2px);
			}
			:host([type='filled'][interactive]:hover:not([disabled])),
			:host([type='outlined'][interactive]:hover:not([disabled])) {
				box-shadow:
					var(--schmancy-sys-elevation-1),
					0 4px 20px -6px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 10%, transparent);
				transform: translateY(-1px);
			}

			:host([interactive]:active:not([disabled])) {
				transform: scale(0.98);
				transition-duration: 100ms;
			}

			:host([dragged]) {
				box-shadow:
					var(--schmancy-sys-elevation-3),
					0 8px 32px -4px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 15%, transparent);
				transform: translateY(-4px);
			}
			/* The drag lift is the one elevation the rule above paints for every
			   type, so it is also the one place a chromeless card would grow a
			   surface behind content that already is one. The translate stays: that
			   moves the content itself, which is what a lift should read as here. */
			:host([type='chromeless'][dragged]) {
				box-shadow: none;
			}

			@media (prefers-reduced-motion: reduce) {
				:host([interactive]:hover:not([disabled])),
				:host([interactive]:active:not([disabled])),
				:host([dragged]) {
					transform: none;
				}
			}

			/* The state layer's opacity is the one axis of that element this block
			   owns rather than its class attribute, because it is driven by the
			   HOST's :hover / :focus-visible / [pressed] and no utility can reach a
			   descendant from an ancestor's state. Authored as hover: and
			   focus-visible: variants ON the layer, it could never fire: the layer
			   is pointer-events:none so it is not a hit-test target and never
			   matches :hover, and the host is the tab stop so the layer is never the
			   focused element. Same idiom the chips already use. Position, corner,
			   ground and transition stay utilities on the element itself. */
			.state-layer {
				opacity: 0;
			}
			/* Hover and focus reach the layer on chromeless alone. The other three
			   answer a pointer with the type-scoped lift and the cursor glow, and
			   keyboard focus with the host's own ring above; a uniform wash on top of
			   those is a second signal for a state already legible, and would change
			   how three shipped types render. A chromeless card paints none of them,
			   so this layer is the only surface its hover has. */
			:host([type='chromeless'][interactive]:not([disabled]):not([pressed]):hover) .state-layer {
				opacity: var(--schmancy-sys-state-opacity-hover, 0.08);
			}
			:host([type='chromeless'][interactive]:not([disabled]):not([pressed]):focus-visible) .state-layer {
				opacity: var(--schmancy-sys-state-opacity-focus, 0.12);
			}
			/* Press is the one state the layer carries for every type — the ripple is
			   a transient, this wash is what holds while the pointer or key is down.
			   The two rules above exclude [pressed] rather than this one out-weighing
			   them, so press supersedes hover instead of racing it on specificity. */
			:host([interactive]:not([disabled])[pressed]) .state-layer {
				opacity: var(--schmancy-sys-state-opacity-pressed, 0.12);
			}

			@keyframes ripple {
				to {
					transform: scale(4);
					opacity: 0;
				}
			}
		`,
	]

	@property({ reflect: true })
	type: 'elevated' | 'filled' | 'outlined' | 'chromeless' = 'elevated'

	@property({ type: Boolean, reflect: true })
	interactive = false

	@property({ type: Boolean, reflect: true })
	disabled = false

	@property({ type: Boolean, reflect: true })
	dragged = false

	/** Setting this makes the whole card act as a link: role + click navigate. */
	@property()
	href?: string

	@property()
	target?: string

	// Not reflected: reflection would emit the `article` default onto every card
	// in every app, and that default is a property sentinel rather than a
	// decision this component made. `updateAriaAttributes` writes the attribute
	// itself, for the interactive roles alone.
	@property({ attribute: 'role' })
	override role = 'article'

	@property({ attribute: 'aria-label' })
	override ariaLabel: string = ''

	@state() private ripples: Array<{ x: number; y: number; id: number }> = []

	private nextRippleId = 0

	/** The role value this component last wrote onto the host, or `''` while the
	 *  host's role is the consumer's own or still the `article` sentinel. A bare
	 *  "we own it" boolean was not enough: the decision has to be re-derivable
	 *  when `href` arrives after mount, and re-deriving needs to know that the
	 *  role standing there is still the one we put there and not something the
	 *  consumer assigned afterwards. */
	private writtenRole = ''

	connectedCallback() {
		super.connectedCallback()

		// The host is the tab stop, so a key pressed on a focused card targets the
		// HOST — and an event path runs outward, never into a shadow descendant.
		// Bound in the template, these handlers sat on the far side of that
		// boundary and Enter/Space activated nothing, on every type. Pointer
		// handlers stay in the template: a click does land on the inner element.
		//
		// That reasoning covers the SHADOW tree and stops there. A card's content
		// is slotted light DOM, and a key event on a nested control bubbles out
		// through this host, so `target === this` is what separates the two: a key
		// event targets the focused node, and with no focusable shadow descendant
		// and no delegatesFocus the card is its own tab stop, so any other target
		// is a second tab stop the consumer put inside the card and Enter there is
		// that control's, including the `preventDefault()` below, which was
		// swallowing the nested control's own default. `keyup` is exempt from the
		// origin check: it only clears the press wash, which is cleanup and can
		// never activate anything.
		merge(fromEvent<KeyboardEvent>(this, 'keydown'), fromEvent<KeyboardEvent>(this, 'keyup'))
			.pipe(
				filter(
					e =>
						(e.key === 'Enter' || e.key === ' ') &&
						this.interactive &&
						!this.disabled &&
						(e.type === 'keyup' || e.target === this),
				),
				takeUntil(this.disconnecting),
			)
			.subscribe(e => {
				if (e.type === 'keyup') return this.setPressed(false)
				e.preventDefault()
				this.setPressed(true)
				const rect = this.getBoundingClientRect()
				this.triggerAction(rect.width / 2, rect.height / 2)
				// A pointer activation hands the consumer a native `click` on its way
				// out of the host; a key press produces none, so a consumer's @click
				// binding worked with a mouse and was silently dead to the keyboard.
				// The card claims role="button", and a button's whole contract is that
				// the two modalities are indistinguishable to a handler. Dispatched
				// here and not in triggerAction: the pointer path's click already
				// exists and a second one would double-fire every consumer.
				// `detail: 0` is the platform's own mark of a non-pointer click.
				this.dispatchEvent(new MouseEvent('click', { bubbles: true, composed: true, cancelable: true, detail: 0 }))
			})
	}

	// Before the render, not after: writing the host's attributes here folds the
	// converter's property write-back into the update already in flight, where
	// the same work from `updated()` schedules a second render cycle per toggle
	// (Lit's `change-in-update` warning). Guarded on the two inputs it reads, so
	// an unrelated property change does not re-derive attributes that cannot have
	// moved.
	protected willUpdate(changed: PropertyValues) {
		super.willUpdate(changed)
		// `href` is in the guard because it is one of the decision's inputs: it
		// picks link over button. Without it a card that gains its href after
		// mount kept announcing itself as a button.
		if (changed.has('interactive') || changed.has('disabled') || changed.has('href')) {
			this.updateAriaAttributes()
		}
	}

	private updateAriaAttributes() {
		const isInteractive = this.interactive && !this.disabled

		// The host itself is the tab stop. The shadow tree holds no focusable
		// element, so `delegatesFocus` would find no delegate and make the host
		// unfocusable outright — tabindex and the focus-visible ring both dead.
		if (isInteractive) {
			this.setAttribute('tabindex', '0')
			// `article` is the property's own default, so it doubles as the sentinel
			// for "the consumer authored no role"; any other value is theirs and is
			// neither overwritten here nor removed below. The third arm re-decides a
			// role this component itself wrote — that is what lets an href arriving
			// after mount turn button into link — while a role the consumer assigned
			// on top of ours stops matching and is left alone from then on.
			if (!this.role || this.role === 'article' || this.role === this.writtenRole) {
				this.role = this.href ? 'link' : 'button'
				this.setAttribute('role', this.role)
				this.writtenRole = this.role
			}
			return
		}

		this.removeAttribute('tabindex')
		if (this.role !== this.writtenRole) return
		// Removal first: the attribute converter writes the property back, so
		// restoring the sentinel afterwards is what survives.
		this.removeAttribute('role')
		this.role = 'article'
		this.writtenRole = ''
	}

	private addRipple(x: number, y: number) {
		const id = this.nextRippleId++
		this.ripples = [...this.ripples, { x, y, id }]

		setTimeout(() => {
			this.ripples = this.ripples.filter(r => r.id !== id)
		}, 600) // M3 medium duration
	}

	private navigate() {
		if (!this.href) return

		if (this.target === '_blank') {
			window.open(this.href, '_blank')
		} else {
			window.location.href = this.href
		}
	}

	private triggerAction(x: number, y: number) {
		this.addRipple(x, y)
		this.navigate()

		this.dispatchEvent(
			new CustomEvent('schmancy-click', {
				detail: { value: this.type },
				bubbles: true,
				composed: true,
			}),
		)
	}

	// The pointer counterpart to the keyboard path's `target === this`, and it
	// cannot be that check: the click handler lives on a shadow element, so its
	// target is either the card's own chrome or the slotted node under the
	// pointer — never the host — and clicking a card's own text MUST still
	// activate it. So the question is not "is the origin the card" but "is there
	// a control between the origin and the card", and it is asked of
	// `composedPath()` rather than `target.closest()` because `target` is
	// retargeted to the nearest node in the listener's own tree: a click on the
	// `<button>` inside a slotted `<schmancy-icon-button>` arrives as the
	// icon-button HOST, which carries neither role nor tabindex, so a `closest()`
	// read finds an ordinary element and the card fires anyway. The path carries
	// that `<button>` at index 0. The walk ends at the card's own shadow root —
	// everything from there outward is the card itself.
	private originatesInNestedControl(e: Event) {
		for (const node of e.composedPath()) {
			if (node === this.shadowRoot || node === this) return false
			if (node instanceof Element && node.matches(NESTED_CONTROL_SELECTOR)) return true
		}
		return false
	}

	private handleClick = (e: MouseEvent) => {
		if (this.disabled || !this.interactive || this.originatesInNestedControl(e)) return

		const rect = this.getBoundingClientRect()
		this.triggerAction(e.clientX - rect.left, e.clientY - rect.top)
	}

	// The host attribute is the whole of the press state: the state layer reads
	// it through a :host([pressed]) rule, so there is nothing left for a
	// reactive field to feed and a re-render per press would produce identical
	// markup.
	private setPressed(pressed: boolean) {
		if (pressed) {
			this.setAttribute('pressed', '')
		} else {
			this.removeAttribute('pressed')
		}
	}

	// Same origin test as the click: a press that will not activate the card must
	// not light the card's state layer either, or the wash promises an activation
	// the release never delivers. The two release handlers stay unconditional —
	// clearing a state that was never set is a no-op, and gating them is how a
	// press gets stranded on.
	private handleMouseDown = (e: MouseEvent) => {
		if (this.disabled || !this.interactive || this.originatesInNestedControl(e)) return
		this.setPressed(true)
	}

	private handleMouseUp = () => this.setPressed(false)
	private handleMouseLeave = () => this.setPressed(false)

	protected render() {
		const isInteractive = this.interactive && !this.disabled
		// cursorGlow prepends a layer painting a primary-tinted radial-gradient
		// background — a surface, and the one the chromeless contract deletes.
		const isGlowing = isInteractive && this.type !== 'chromeless'
		// A chromeless card asserts no corner of its own, so its layers take the
		// host's — whatever a consumer's own `rounded-*` set it to — instead of a
		// rung that would strand them behind the focus ring at xl.
		// Every type's inner layers (state layer, ripple clip, slot wrapper) take
		// the host's own border-radius rather than a hardcoded rung: a consumer
		// that overrides the host's corner with its own `rounded-*` — order-card
		// -shell fuses a card's left edge flat against a tone-spine stub with
		// `rounded-l-none rounded-r-xl` — needs every layer inside to agree with
		// that shape, or the state layer and ripple clip paint the un-overridden
		// xl rung on a corner the host itself now draws square.
		const corner = 'rounded-[inherit]'

		return html`
			<div
				${isGlowing ? cursorGlow({ radius: 200, intensity: 0.1 }) : ''}
				class="relative w-full h-full ${corner} ${isInteractive ? 'cursor-pointer' : ''}"
				@click=${this.handleClick}
				@mousedown=${this.handleMouseDown}
				@mouseup=${this.handleMouseUp}
				@mouseleave=${this.handleMouseLeave}
				aria-label=${ifDefined(this.ariaLabel)}
				aria-disabled=${this.disabled ? 'true' : 'false'}
			>
				<div
					class=${this.classMap({
						'state-layer absolute inset-0 pointer-events-none transition-opacity duration-200 bg-surface-on': true,
						[corner]: true,
					})}
				></div>

				${
					this.interactive && this.ripples.length
						? html`
								<div class="absolute inset-0 ${corner} overflow-hidden pointer-events-none">
									${this.ripples.map(
										r => html`
											<span
												class="absolute rounded-full scale-0 animate-[ripple_600ms_linear] bg-surface-on opacity-[var(--schmancy-sys-state-opacity-pressed,0.12)] w-5 h-5 -ml-2.5 -mt-2.5"
												style="left: ${r.x}px; top: ${r.y}px"
											></span>
										`,
									)}
								</div>
							`
						: ''
				}

				<div class="relative h-full w-full ${corner}">
					<slot></slot>
				</div>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-card': SchmancyCard
	}
}
